import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarCheck, ShieldCheck, Info } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import { findBookable } from "../data/consultants";
import { trackEvent } from "../components/Analytics";
import { ReasonPicker, reasonTitles } from "../components/booking/ReasonPicker";
import { ConsultantPicker } from "../components/booking/ConsultantPicker";

const DEFAULT_CALENDLY = "https://calendly.com/cambclinic/new-meeting";

export default function BookAppointmentCalendly() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const consultantSlug = params.get("consultant") || "";
  const consultant = useMemo(() => findBookable(consultantSlug), [consultantSlug]);
  const calendlyUrl = (consultant && consultant.bookingUrl) || DEFAULT_CALENDLY;
  // a1 pre-fills the first Calendly question ("Consultant"); utm_campaign is also saved on the Calendly booking
  const consultantLabel = consultant ? consultant.name : "First available consultant";
  const [reasons, setReasons] = useState([]);
  const [pick, setPick] = useState(consultant ? consultant.slug : "");
  const choosePick = (slug) => {
    setPick(slug);
    setParams(slug === "any" ? {} : { consultant: slug }, { replace: true });
  };
  const [showCalendar, setShowCalendar] = useState(false);
  // a2 pre-fills the second Calendly question ("Reason for visit"); utm_content keeps the ticked reasons on the booking
  const embedUrl = `${calendlyUrl}?hide_gdpr_banner=1&primary_color=8C6643&a1=${encodeURIComponent(consultantLabel)}&a2=${encodeURIComponent(reasonTitles(reasons).join("; "))}&utm_source=website&utm_campaign=${encodeURIComponent(consultantSlug || "any")}&utm_content=${encodeURIComponent(reasons.join(","))}`;
  const widgetRef = useRef(null);

  // Calendly only auto-initialises widgets present on script load, so init manually when revealed
  useEffect(() => {
    if (!showCalendar || !widgetRef.current || !window.Calendly) return;
    widgetRef.current.replaceChildren();
    window.Calendly.initInlineWidget({ url: embedUrl, parentElement: widgetRef.current });
  }, [showCalendar, embedUrl]);

  // Load Calendly script once
  useEffect(() => {
    if (document.querySelector('script[data-calendly-embed]')) return;
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    script.setAttribute("data-calendly-embed", "true");
    document.body.appendChild(script);
  }, []);

  // Listen for booking event from Calendly widget
  useEffect(() => {
    const isCalendlyEvent = (e) => e?.data?.event && typeof e.data.event === "string"
      && e.data.event.indexOf("calendly.") === 0;
    const onMessage = (e) => {
      if (!isCalendlyEvent(e)) return;
      if (e.data.event === "calendly.event_scheduled") {
        const payload = e.data.payload || {};
        const eventUri = payload.event && payload.event.uri;
        const inviteeUri = payload.invitee && payload.invitee.uri;
        if (eventUri && inviteeUri) {
          trackEvent("calendly_scheduled", { consultant: consultantSlug || "any" });
          const q = new URLSearchParams({
            event_uri: eventUri,
            invitee_uri: inviteeUri,
            consultant: consultantSlug || "",
            reasons: reasons.join(","),
          }).toString();
          navigate(`/complete-payment?${q}`);
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [consultantSlug, navigate, reasons]);

  return (
    <div data-testid="book-appointment-page">
      <SEO
        title="Book an Appointment"
        description="Book a consultation with Cambridge Cardiovascular Prevention Clinic. Choose a time that suits you, then confirm your booking with secure payment."
        path="/enquiry"
      />

      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-16 max-w-5xl">
          <div className="eyebrow">{consultant ? `Booking · ${consultant.name}` : "Book an appointment"}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            Choose a time. <em className="font-serif text-bronze-dark">Then confirm with payment.</em>
          </h1>
          <p className="mt-6 text-ink-muted text-lg max-w-3xl leading-relaxed">
            {consultant
              ? <>Booking a 1-hour new consultation with <b>{consultant.name}</b>. Pick a slot below and you&apos;ll be taken straight to secure payment. <b>Your booking is only confirmed once payment is received</b> - a confirmation email and calendar invite will follow.</>
              : <>Pick a slot below and you&apos;ll be taken straight to secure payment. <b>Your booking is only confirmed once payment is received</b> - a confirmation email and calendar invite will follow.</>}
          </p>
        </div>
      </section>

      <section className="container-ccc py-12 max-w-6xl">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            {!showCalendar ? (
              <>
                <ReasonPicker value={reasons} onChange={setReasons} />
                <ConsultantPicker value={pick} onChange={choosePick} />
                <button type="button" disabled={!reasons.length || !pick} onClick={() => setShowCalendar(true)}
                  data-testid="reason-continue-btn" className="btn-primary disabled:opacity-50">
                  {!reasons.length ? "Tick at least one reason to continue" : !pick ? "Choose a consultant to continue" : "Continue - choose a time"}
                </button>
              </>
            ) : (
              <>
                <div data-testid="reason-summary" className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-ink-muted">Coming about:</span>
                  {reasonTitles(reasons).map((t) => <span key={t} className="px-2.5 py-1 rounded-full bg-cream-100 border border-bronze-light/50 text-xs">{t}</span>)}
                  <span className="text-ink-muted ml-3">With:</span>
                  <span data-testid="consultant-summary" className="px-2.5 py-1 rounded-full bg-cream-100 border border-bronze-light/50 text-xs">{consultantLabel}</span>
                  <button type="button" onClick={() => setShowCalendar(false)} data-testid="reason-change-btn" className="text-xs text-bronze-dark underline underline-offset-4 ml-1">Change</button>
                </div>
                <div className="eyebrow">Step 3 · Choose a time</div>
                <div
                  ref={widgetRef}
                  className="calendly-inline-widget border border-bronze-light/30 rounded-md overflow-hidden bg-white"
                  data-testid="calendly-embed"
                  data-url={embedUrl}
                  style={{ minWidth: "320px", height: "820px" }}
                />
              </>
            )}
            <p className="mt-4 text-xs text-ink-muted flex items-start gap-2">
              <Info size={14} weight="duotone" className="text-bronze-dark mt-0.5 shrink-0" />
              After you pick a slot, you&apos;ll confirm your details and go to secure Stripe payment. If you don&apos;t complete payment within 15 minutes the slot is released automatically.
            </p>
          </div>

          <aside className="lg:col-span-4 space-y-5">
            <div className="p-6 bg-cream-100 border border-bronze-light/40 rounded-md">
              <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                <CalendarCheck size={16} weight="duotone" /> How it works
              </div>
              <ol className="mt-4 space-y-3 text-sm text-ink-muted list-decimal list-inside">
                <li>Tick what you&apos;d like to see us about.</li>
                <li>Choose your consultant (or first available).</li>
                <li>Pick a date and time from the calendar.</li>
                <li>Enter your name and email in Calendly.</li>
                <li>You&apos;ll be redirected to secure payment (£300 for a 1-hour new consultation only - any tests or scans are charged separately).</li>
                <li>We see self-pay patients only - we don&apos;t accept private medical insurance.</li>
                <li>Once paid, we email your confirmation + calendar invite.</li>
              </ol>
            </div>

            <div className="p-6 bg-white border border-stone-200/60 rounded-md">
              <div className="eyebrow flex items-center gap-2 text-bronze-dark">
                <ShieldCheck size={16} weight="duotone" /> Payment &amp; refunds
              </div>
              <ul className="mt-4 space-y-2 text-sm text-ink-muted">
                <li>Card payments processed by Stripe with 3-D Secure.</li>
                <li>Full refund if you cancel 48+ hours ahead.</li>
                <li>50% refund between 24-48 hours before.</li>
                <li>No refund inside 24 hours.</li>
              </ul>
            </div>

          </aside>
        </div>
      </section>
    </div>
  );
}
